import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Appearance: 'system' (default, follows the device), 'light' or 'dark'.
// The choice is kept on this device only (localStorage 'pa_theme') because the visitor asked for it;
// it contains no personal data. public/theme-init.js applies it before the page paints.
const KEY = 'pa_theme';
const ThemeContext = createContext(null);
const mq = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null);

function readPref() {
  try { const v = localStorage.getItem(KEY); return v === 'light' || v === 'dark' ? v : 'system'; } catch (e) { return 'system'; }
}
function apply(pref) {
  const el = document.documentElement;
  if (pref === 'system') el.removeAttribute('data-theme'); else el.setAttribute('data-theme', pref);
}
function cssColor(name) {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(`--pa-${name}`).trim();
  if (!raw) return undefined;
  return raw.startsWith('rgb') ? raw : `rgb(${raw.split(/\s+/).join(', ')})`;
}
const CHART_TOKENS = ['ink', 'ink-muted', 'ink-faint', 'line', 'surface', 'primary', 'night', 'accent', 'chart-user', 'chart-reference', 'chart-third', 'chart-grid', 'chart-text', 'ok', 'warn', 'bad'];

export function ThemeProvider({ children }) {
  const [pref, setPref] = useState(readPref);
  const [systemDark, setSystemDark] = useState(() => !!mq()?.matches);
  const [colors, setColors] = useState({});

  useEffect(() => {
    const m = mq();
    if (!m) return undefined;
    const on = (e) => setSystemDark(e.matches);
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, []);

  const resolved = pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;

  useEffect(() => {
    apply(pref);
    // Read the live token values (after the attribute/media query has taken effect) for canvas/SVG charts.
    const next = {};
    CHART_TOKENS.forEach((t) => { next[t] = cssColor(t); });
    setColors(next);
  }, [pref, resolved]);

  const choose = useCallback((v) => {
    setPref(v);
    try { if (v === 'system') localStorage.removeItem(KEY); else localStorage.setItem(KEY, v); } catch (e) { /* storage blocked */ }
  }, []);

  const value = useMemo(() => ({ preference: pref, resolved, setPreference: choose, colors }), [pref, resolved, choose, colors]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext) || { preference: 'system', resolved: 'light', setPreference: () => {}, colors: {} };
}
